/**
 * Dashboard status utilities
 * Provides configuration and helper functions for application and TFA statuses
 */

/**
 * TFA Status configuration with display labels and colors
 * Color scheme: Green (success), Red (error), Yellow (warning), Gray (neutral)
 */ 
export const tfaStatusConfig = {
  // Green - Success states
  "TFA Approved": {
    label: "TFA Approved",
    color: "#3a833c", // Phanatic Green - Philly Style Guide
    bgColor: "#dcf8d8", // Phanatic Green Light - Philly Style Guide
  },
  "Application Approved": {
    label: "Application Approved",
    color: "#3a833c", // Phanatic Green - Philly Style Guide
    bgColor: "#dcf8d8", // Phanatic Green Light - Philly Style Guide
  },

  // Red - Error/Critical states
  "TFA Ineligible": {
    label: "TFA Ineligible",
    color: "#cc3000", // Love Park Red - Philly Style Guide
    bgColor: "#fee7e7", // Love Park Red Light - Philly Style Guide
  },
  "TFA Denied": {
    label: "TFA Denied",
    color: "#cc3000", // Love Park Red - Philly Style Guide
    bgColor: "#fee7e7", // Love Park Red Light - Philly Style Guide
  },
  "Landlord Did Not Accept TFA": {
    label: "Landlord Did Not Accept TFA",
    color: "#cc3000", // Love Park Red - Philly Style Guide
    bgColor: "#fee7e7", // Love Park Red Light - Philly Style Guide
  },
  "Tenant Did Not Accept TFA": {
    label: "Tenant Did Not Accept TFA",
    color: "#cc3000", // Love Park Red - Philly Style Guide
    bgColor: "#fee7e7", // Love Park Red Light - Philly Style Guide
  },
  "Error Flagged by Vendor": {
    label: "Error Flagged by Vendor",
    color: "#cc3000", // Love Park Red - Philly Style Guide
    bgColor: "#fee7e7", // Love Park Red Light - Philly Style Guide
  },

  // Yellow - Warning states
  "TFA Under Review": {
    label: "TFA Under Review",
    color: "#f99300", // Flyers Orange - Philly Style Guide
    bgColor: "#fff7d0", // Bell Yellow Light - Philly Style Guide
  },
  "Application Approved, Awaiting Payment": {
    label: "Application Approved, Awaiting Payment",
    color: "#f99300", // Flyers Orange - Philly Style Guide
    bgColor: "#fff7d0", // Bell Yellow Light - Philly Style Guide
  },
  "Tenant Form Not Submitted": {
    label: "Tenant Form Not Submitted",
    color: "#f99300", // Flyers Orange - Philly Style Guide
    bgColor: "#fff7d0", // Bell Yellow Light - Philly Style Guide
  },

  // Gray - Neutral/Inactive states
  "N/A": {
    label: "N/A",
    color: "#444444", // Dark Gray - Philly Style Guide
    bgColor: "#f0f0f0", // Ghost Gray - Philly Style Guide
  },
  "EDP Application Withdrawn": {
    label: "EDP Application Withdrawn",
    color: "#444444", // Dark Gray - Philly Style Guide
    bgColor: "#f0f0f0", // Ghost Gray - Philly Style Guide
  },
  "Landlord withdrew TFA application": {
    label: "Landlord withdrew TFA application",
    color: "#444444", // Dark Gray - Philly Style Guide
    bgColor: "#f0f0f0", // Ghost Gray - Philly Style Guide
  },
};



/**
 * Application Status configuration with display labels and colors
 * Color scheme: Green (success), Red (error), Yellow (warning), Gray (neutral)
 */
export const applicationStatusConfig = {
  // Green - Success states
  Approved: {
    label: "Approved",
    color: "#3a833c", // Phanatic Green - Philly Style Guide
    bgColor: "#dcf8d8", // Phanatic Green Light - Philly Style Guide
  },
  Completed: {
    label: "Completed",
    color: "#3a833c", // Phanatic Green - Philly Style Guide
    bgColor: "#dcf8d8", // Phanatic Green Light - Philly Style Guide
  },

  // Red - Error/Critical states
  Denied: {
    label: "Denied",
    color: "#cc3000", // Love Park Red - Philly Style Guide
    bgColor: "#fee7e7", // Love Park Red Light - Philly Style Guide
  },
  "Needs Correction": {
    label: "Needs Correction",
    color: "#cc3000", // Love Park Red - Philly Style Guide
    bgColor: "#fee7e7", // Love Park Red Light - Philly Style Guide
  },

  // Yellow - Warning states
  Expired: {
    label: "Expired",
    color: "#f99300", // Flyers Orange - Philly Style Guide
    bgColor: "#fff7d0", // Bell Yellow Light - Philly Style Guide
  },
  "Pending Completion by Owner": {
    label: "Pending Completion by Owner",
    color: "#f99300", // Flyers Orange - Philly Style Guide
    bgColor: "#fff7d0", // Bell Yellow Light - Philly Style Guide
  },
  "Under Review": {
    label: "Under Review",
    color: "#f99300", // Flyers Orange - Philly Style Guide
    bgColor: "#fff7d0", // Bell Yellow Light - Philly Style Guide
  },

};

/**
 * Get application status display information
 * @param {string} statusKey - The status key
 * @returns {object} Status configuration object with label, color, and bgColor
 */
export function getApplicationStatusInfo(statusKey) {
  return applicationStatusConfig[statusKey] || {
    label: statusKey,
    color: "#444444",
    bgColor: "#f0f0f0",
  };
}

/**
 * Get TFA status display information
 * @param {string} statusKey - The status key
 * @returns {object} Status configuration object with label, color, and bgColor
 */
export function getTfaStatusInfo(statusKey) {
  return tfaStatusConfig[statusKey] || {
    label: statusKey,
    color: "#444444",
    bgColor: "#f0f0f0",
  };
}

/**
 * Filter applications by search term
 * Searches in address and EDP number fields
 * @param {string} searchTerm - Search term to filter by
 * @param {array} applications - Array of application objects to filter
 * @returns {array} Filtered applications
 */
export function filterApplications(searchTerm, applications) {
  if (!searchTerm) return applications;

  const term = searchTerm.toLowerCase();
  return applications.filter(
    (app) =>
      app.unit_address?.toLowerCase().includes(term) ||
      app.edp_number?.toLowerCase().includes(term)
  );
}
